import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, ArrowsClockwise, ArrowUp, ArrowDown, Minus, CheckCircle, WarningCircle, XCircle, PaperPlaneTilt, GearSix, FileText, CursorClick, Files, Warning, DownloadSimple, NotePencil, Stethoscope, CaretDown } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { CountryBars, DeviceBars, TopPagesList } from "../components/WebInsights";

const STATUS = {
  green: { dot: "bg-emerald-400", ring: "shadow-[0_0_8px_rgba(52,211,153,0.6)]", label: "OK" },
  amber: { dot: "bg-amber-400", ring: "shadow-[0_0_8px_rgba(251,191,36,0.6)]", label: "Da controllare" },
  red: { dot: "bg-red-500", ring: "shadow-[0_0_8px_rgba(239,68,68,0.7)]", label: "Errore" },
};

const Sparkline = ({ data }) => {
  const max = Math.max(...(data.length ? data : [0]), 1);
  return (
    <div className="flex items-end gap-[2px] h-8 w-24">
      {(data.length ? data : Array(28).fill(0)).map((v, i) => (
        <div key={i} className="flex-1 rounded-sm bg-emerald-400/70" style={{ height: `${Math.max((v / max) * 100, 4)}%`, opacity: v === 0 ? 0.15 : 1 }} />
      ))}
    </div>
  );
};

const COMPARE_COLORS = { "risorice.it": "#38BDF8", "michelchef.it": "#34D399", "commpaq.it": "#FBBF24", "paqgroup.it": "#A78BFA", "propaq.it": "#FB7185" };
const DOT_COLOR = { ok: "#34D399", warn: "#FBBF24", down: "#EF4444", unknown: "#71717A" };
const DOT_LABEL = { ok: "OK", warn: "Attenzione", down: "Non raggiungibile", unknown: "Sconosciuto" };

const CompareChart = ({ dates, series }) => {
  const W = 600, H = 150, P = 4;
  const max = Math.max(...Object.values(series).flat(), 1);
  const toPoints = (vals) =>
    vals.map((v, i) => `${P + (i / Math.max(vals.length - 1, 1)) * (W - 2 * P)},${H - P - (v / max) * (H - 2 * P)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" data-testid="compare-chart" preserveAspectRatio="none">
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1={P} x2={W - P} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      ))}
      {Object.entries(series).map(([domain, vals]) => (
        <polyline key={domain} points={toPoints(vals)} fill="none" stroke={COMPARE_COLORS[domain] || "#fff"}
          strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
      ))}
    </svg>
  );
};

const Kpi = ({ label, value, alert, testId }) => (
  <div className="p-6 rounded-lg bg-surface border border-surface-border" data-testid={testId}>
    <p className="text-[10px] tracking-[0.25em] uppercase text-white/40">{label}</p>
    <p className={`font-heading text-4xl font-bold tracking-tighter mt-2 ${alert ? "text-red-500" : "text-white"}`}>{value}</p>
  </div>
);

export default function WebPage() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [detailSite, setDetailSite] = useState(null);
  const [indexUrl, setIndexUrl] = useState("");
  const [indexing, setIndexing] = useState(false);
  const [bulkIndexing, setBulkIndexing] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [inspectResult, setInspectResult] = useState(null);

  const handleInspect = async () => {
    setInspecting(true);
    setInspectResult(null);
    try {
      const { data: res } = await api.post("/admin/web/inspect", { url: indexUrl });
      setInspectResult(res);
      if (detailSite) {
        const { data: d } = await api.get("/admin/web/overview");
        setData(d);
        const fresh = d.sites?.find((s) => s.domain === detailSite.domain);
        if (fresh) setDetailSite(fresh);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInspecting(false);
    }
  };
  const [saJson, setSaJson] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [cfgExpanded, setCfgExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyDays, setHistoryDays] = useState(90);
  const [compare, setCompare] = useState(null);
  const [compareDays, setCompareDays] = useState(90);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/admin/web/notes/${detailSite.domain}`, { note: noteText });
      toast.success(noteText.trim() ? "Promemoria salvato" : "Promemoria rimosso");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingNote(false);
    }
  };

  const loadCompare = (days) => {
    api.get(`/admin/web/compare?days=${days}`).then(({ data }) => setCompare(data)).catch(() => {});
  };
  useEffect(() => { loadCompare(90); }, []);

  const loadHistory = (domain, days) => {
    setHistory(null);
    api.get(`/admin/web/history/${domain}?days=${days}`).then(({ data }) => setHistory(data)).catch(() => setHistory({ rows: [] }));
  };

  const load = () => api.get("/admin/web/overview").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    const prev = data?.updated_at;
    try {
      await api.post("/admin/web/refresh");
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: d } = await api.get("/admin/web/overview");
        if (d.updated_at && d.updated_at !== prev) {
          setData(d);
          loadCompare(compareDays);
          toast.success("Dati aggiornati da Search Console");
          setRefreshing(false);
          return;
        }
      }
      toast.error("L'aggiornamento sta impiegando più del previsto: ricarica la pagina tra qualche minuto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setRefreshing(false);
  };

  const [doctorOpen, setDoctorOpen] = useState(false);
  const [doctorRunning, setDoctorRunning] = useState(false);
  const [doctorData, setDoctorData] = useState(null);
  const [doctorHistory, setDoctorHistory] = useState([]);

  const loadDoctorHistory = () => api.get("/admin/web/doctor/history").then(({ data: h }) => setDoctorHistory(h)).catch(() => {});
  useEffect(() => { loadDoctorHistory(); }, []);

  const [autoIndexHistory, setAutoIndexHistory] = useState([]);
  useEffect(() => {
    api.get("/admin/web/auto-index/history").then(({ data: h }) => setAutoIndexHistory(h)).catch(() => {});
  }, []);

  const runSiteDoctor = async () => {
    setDoctorOpen(true);
    setDoctorRunning(true);
    try {
      const { data: d } = await api.post("/admin/web/doctor");
      setDoctorData(d);
      loadDoctorHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setDoctorOpen(false);
    } finally {
      setDoctorRunning(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSavingCfg(true);
    try {
      const { data: res } = await api.post("/admin/web/config", { service_account_json: saJson });
      toast.success(`Credenziali salvate (${res.client_email}). Recupero dati in corso...`);
      setSaJson("");
      setTimeout(load, 8000);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingCfg(false);
    }
  };

  const handleIndexMissing = async () => {
    setBulkIndexing(true);
    try {
      const { data: res } = await api.post(`/admin/web/${detailSite.domain}/index-missing`);
      if (res.ok?.length) toast.success(res.message, { duration: 10000 });
      else toast.error(res.message, { duration: 10000 });
      if (res.ok?.length && res.failed?.length) toast.error(`${res.failed.length} pagine rifiutate: ${res.failed[0].error}`, { duration: 10000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBulkIndexing(false);
    }
  };

  const handleRequestIndexing = async () => {
    setIndexing(true);
    try {
      const { data: res } = await api.post("/admin/web/request-indexing", { url: indexUrl });
      toast.success(res.message);
      setIndexUrl("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setIndexing(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/admin/web/export?days=365", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `seo_storico_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Esportazione CSV avviata");
    } catch (err) {
      toast.error("Esportazione fallita");
    }
  };

  const openDetail = (site) => {
    setDetailSite(site);
    setIndexUrl(`https://${site.domain}/`);
    setInspectResult(null);
    setNoteText(data?.notes?.[site.domain]?.note || "");
    setHistoryDays(90);
    loadHistory(site.domain, 90);
  };

  const changeHistoryDays = (days) => { setHistoryDays(days); loadHistory(detailSite.domain, days); };

  const sites = data?.sites?.length ? data.sites : (data?.monitored_sites || []).map((d) => ({
    domain: d, status: "amber", sitemap_status: "—", sitemap_detail: data?.configured ? "In attesa del primo aggiornamento" : "Da configurare",
    indexed: 0, total_pages: 0, clicks: 0, trend: "flat", daily_clicks: [], errors: 0, error: null, placeholder: true,
  }));
  const s = data?.summary;
  const histDomains = doctorHistory.length ? Object.keys(doctorHistory[0].summary || {}) : [];

  const actionFor = (site) => {
    if (site.status === "red") return { label: "Risolvi", cls: "bg-red-600 hover:bg-red-700 text-white" };
    if (site.status === "amber" && site.indexed === 0) return { label: "Richiedi indicizzazione", cls: "border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 bg-transparent" };
    return { label: "Dettagli", cls: "bg-white text-black hover:bg-gray-200" };
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="web-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10 animate-fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Monitoraggio SEO</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">WebSiti</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-white/40 w-full sm:w-auto" data-testid="web-last-updated">
            {data?.updated_at ? `Ultimo aggiornamento: ${new Date(data.updated_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : "Nessun dato ancora"}
          </p>
          <Button data-testid="web-refresh-button" onClick={handleRefresh} disabled={refreshing || !data?.configured}
            variant="ghost" className="h-10 px-5 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
            <ArrowsClockwise size={15} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Aggiornamento..." : "Aggiorna ora"}
          </Button>
          <Button data-testid="web-doctor-button" onClick={runSiteDoctor} disabled={doctorRunning}
            className="h-10 px-5 bg-white text-black hover:bg-gray-200 rounded-full text-xs font-semibold">
            <Stethoscope size={15} className={`mr-1.5 ${doctorRunning ? "animate-pulse" : ""}`} /> {doctorRunning ? "Visita..." : "Doctor"}
          </Button>
        </div>
      </div>

      {data && !data.configured && (
        <div className="mb-8 p-4 rounded-lg border border-amber-400/30 bg-amber-400/5 text-amber-300 text-sm flex items-center gap-3 animate-fade-up" data-testid="web-not-configured-banner">
          <Warning size={20} weight="duotone" className="shrink-0" />
          Dashboard non ancora collegata a Google: incolla il JSON del service account nella sezione Configurazione qui sotto. I dati mostrati sono vuoti.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 animate-fade-up">
        <Kpi label="Siti monitorati" value={s ? s.sites : sites.length} testId="kpi-sites" />
        <Kpi label="Pagine indicizzate" value={s ? `${s.indexed_total}/${s.pages_total}` : "—"} testId="kpi-indexed" />
        <Kpi label="Clic (28gg)" value={s ? s.clicks_total : "—"} testId="kpi-clicks" />
        <Kpi label="Errori attivi" value={s ? s.active_errors : "—"} alert={s?.active_errors > 0} testId="kpi-errors" />
      </div>

      <div className="space-y-3 mb-12">
        {sites.map((site, i) => {
          const st = STATUS[site.status] || STATUS.amber;
          const action = actionFor(site);
          return (
            <div key={site.domain} data-testid={`site-row-${site.domain}`}
              className={`rounded-lg bg-surface border p-5 flex flex-wrap items-center gap-x-6 gap-y-3 animate-fade-up ${site.status === "red" ? "border-red-500/40 border-l-4 border-l-red-500 bg-red-500/[0.04]" : "border-surface-border"}`}
              style={{ animationDelay: `${i * 60}ms` }}>
              <span title={st.label} className={`w-2.5 h-2.5 rounded-full shrink-0 ${st.dot} ${st.ring}`} data-testid={`site-status-${site.domain}`} />
              <div className="w-40 min-w-0">
                <p className="font-heading font-semibold tracking-tight text-lg truncate flex items-center gap-1.5">
                  {site.domain}
                  {data?.notes?.[site.domain] && (
                    <NotePencil size={14} weight="duotone" className="text-sky-400 shrink-0" data-testid={`site-note-icon-${site.domain}`} title={data.notes[site.domain].note} />
                  )}
                </p>
                <p className={`text-xs truncate ${site.status === "red" ? "text-red-400" : "text-white/40"}`}>
                  {site.error ? "Errore di accesso" : `Sitemap: ${site.sitemap_status}`}
                </p>
              </div>
              <div className="text-center">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/35">Indicizzate</p>
                <p className="font-heading font-semibold text-lg">{site.placeholder ? "—" : `${site.indexed}/${site.total_pages || "?"}`}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/35">Clic 28gg</p>
                <p className="font-heading font-semibold text-lg flex items-center justify-center gap-1">
                  {site.placeholder ? "—" : site.clicks}
                  {!site.placeholder && site.trend === "up" && <ArrowUp size={14} weight="bold" className="text-emerald-400" />}
                  {!site.placeholder && site.trend === "down" && <ArrowDown size={14} weight="bold" className="text-red-400" />}
                  {!site.placeholder && site.trend === "flat" && <Minus size={14} weight="bold" className="text-white/30" />}
                </p>
              </div>
              <Sparkline data={site.daily_clicks || []} />
              <div className="ml-auto">
                <Button data-testid={`site-action-${site.domain}`} onClick={() => openDetail(site)} disabled={site.placeholder}
                  className={`h-9 px-5 rounded-full text-xs font-semibold ${action.cls}`}>
                  {action.label}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-8 rounded-lg bg-surface border border-surface-border mb-8 animate-fade-up" data-testid="web-compare-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Confronto siti</h2>
            <p className="text-sm text-slate-400 mt-0.5">Andamento clic dei 5 siti a confronto.</p>
          </div>
          <div className="flex gap-1 items-center">
            <button type="button" data-testid="web-export-button" onClick={handleExport}
              className="h-7 px-3 mr-2 rounded-full text-[11px] font-semibold border border-white/15 text-white/60 hover:text-white hover:bg-white/5 transition-colors inline-flex items-center gap-1">
              <DownloadSimple size={13} /> Esporta CSV
            </button>
            {[{ d: 28, l: "28gg" }, { d: 90, l: "3 mesi" }, { d: 180, l: "6 mesi" }, { d: 365, l: "1 anno" }].map((p) => (
              <button key={p.d} type="button" data-testid={`compare-period-${p.d}`}
                onClick={() => { setCompareDays(p.d); loadCompare(p.d); }}
                className={`h-7 px-3 rounded-full text-[11px] font-semibold border transition-colors ${compareDays === p.d ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:text-white"}`}>
                {p.l}
              </button>
            ))}
          </div>
        </div>
        {compare?.has_data ? (
          <>
            <CompareChart dates={compare.dates} series={compare.series} />
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4" data-testid="compare-legend">
              {Object.entries(compare.totals).map(([domain, total]) => (
                <span key={domain} className="inline-flex items-center gap-1.5 text-xs text-white/60">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: COMPARE_COLORS[domain] }} />
                  {domain} <span className="text-white font-semibold">{total}</span>
                </span>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-white/40 py-6 text-center" data-testid="compare-empty">
            Nessuno storico ancora: il confronto si costruisce a ogni aggiornamento giornaliero dei dati.
          </p>
        )}
      </div>

      <div className="rounded-lg bg-surface border border-surface-border animate-fade-up overflow-hidden" data-testid="web-config-card">
        <button type="button" data-testid="web-config-toggle" onClick={() => setCfgExpanded((v) => !v)}
          className="w-full flex items-center gap-4 p-5 text-left hover:bg-white/[0.03] transition-colors">
          <GearSix size={28} weight="duotone" className="text-sky-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-xl font-semibold tracking-tight">Configurazione</h2>
          </div>
          {data?.configured ? (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 shrink-0"><CheckCircle size={14} weight="fill" /> Collegato</span>
          ) : (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 shrink-0"><XCircle size={14} weight="fill" /> Non configurato</span>
          )}
          <CaretDown size={16} weight="bold" className={`text-white/40 shrink-0 transition-transform duration-200 ${cfgExpanded ? "rotate-180" : ""}`} />
        </button>
        {cfgExpanded && (
          <div className="px-5 pb-6 pt-1 border-t border-white/5" data-testid="web-config-details">
            <p className="text-sm text-slate-400 my-4">
              {data?.configured
                ? <>Collegato al service account <span className="text-white/80">{data.client_email}</span>. Puoi incollare un nuovo JSON per sostituirlo.</>
                : "Incolla qui l'intero contenuto del file JSON del service account scaricato da Google Cloud (guida nella chat)."}
            </p>
            <form onSubmit={handleSaveConfig} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Service account JSON</Label>
                <Textarea required data-testid="gsc-json-textarea" value={saJson} onChange={(e) => setSaJson(e.target.value)} rows={5}
                  placeholder='{"type":"service_account","project_id":"...","client_email":"...", ...}'
                  className="bg-transparent border-white/10 text-white placeholder:text-white/25 font-mono text-xs" />
              </div>
              <Button type="submit" disabled={savingCfg} data-testid="gsc-save-button" className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
                {savingCfg ? "Salvataggio..." : "Salva e collega"}
              </Button>
            </form>
            <p className="text-[11px] text-white/35 mt-4">Il JSON viene cifrato nel database. Siti monitorati: {(data?.monitored_sites || []).join(", ")}. Aggiornamento automatico ogni giorno alle 05:00 UTC.</p>
          </div>
        )}
      </div>

      <Dialog open={!!detailSite} onOpenChange={(o) => !o && setDetailSite(null)}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="site-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <Globe size={20} weight="duotone" className="text-sky-400" /> {detailSite?.domain}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Dettagli Search Console degli ultimi 28 giorni.</DialogDescription>
          </DialogHeader>
          {detailSite && (
            <div className="space-y-4 mt-1">
              {detailSite.error && (
                <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 text-sm flex gap-2" data-testid="site-detail-error">
                  <XCircle size={18} weight="fill" className="shrink-0 mt-0.5" /> {detailSite.error}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 flex items-center gap-1"><FileText size={11} /> Sitemap</p>
                  <p className="mt-1 flex items-center gap-1.5">
                    {detailSite.sitemap_status === "riuscita" ? <CheckCircle size={14} weight="fill" className="text-emerald-400" /> : <WarningCircle size={14} weight="fill" className={detailSite.errors ? "text-red-400" : "text-amber-400"} />}
                    {detailSite.sitemap_detail}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 flex items-center gap-1"><Files size={11} /> Pagine</p>
                  <p className="mt-1">{detailSite.indexed} indicizzate su {detailSite.total_pages || "?"} in sitemap</p>
                  <p className="text-[10px] text-white/35 mt-0.5">{detailSite.index_method === "inspection" ? "Conteggio esatto (URL Inspection)" : "Stima da impressioni"}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 col-span-2">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 flex items-center gap-1"><CursorClick size={11} /> Performance 28gg</p>
                  <p className="mt-1">{detailSite.clicks} clic ({detailSite.trend === "up" ? "in crescita" : detailSite.trend === "down" ? "in calo" : "stabile"} vs {detailSite.clicks_prev} del periodo precedente) — {detailSite.impressions || 0} impressioni</p>
                </div>
              </div>
              {detailSite.top_queries?.length > 0 && (
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="top-queries-section">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-2">Ricerche Google top (28gg)</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/30 px-2">
                      <span className="flex-1">Ricerca</span><span className="w-12 text-right">Clic</span><span className="w-16 text-right">Impress.</span>
                    </div>
                    {detailSite.top_queries.map((q) => (
                      <div key={q.query} className="flex items-center gap-2 text-xs rounded-md px-2 py-1.5 bg-white/[0.02]" data-testid={`top-query-${q.query}`}>
                        <span className="flex-1 truncate text-white/80">"{q.query}"</span>
                        <span className="w-12 text-right font-semibold text-emerald-400">{q.clicks}</span>
                        <span className="w-16 text-right text-white/50">{q.impressions}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detailSite.query_compare?.length > 0 && (
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="query-compare-section">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-2">Parole chiave in crescita / calo</p>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/30 px-2">
                      <span className="flex-1">Ricerca</span><span className="w-20 text-right">Clic prima → ora</span><span className="w-14 text-right">Var.</span>
                    </div>
                    {detailSite.query_compare.map((q) => (
                      <div key={q.query} className="flex items-center gap-2 text-xs rounded-md px-2 py-1.5 bg-white/[0.02]" data-testid={`query-compare-${q.query}`}>
                        <span className="flex-1 truncate text-white/80">"{q.query}"</span>
                        <span className="w-20 text-right text-white/50">{q.clicks_prev} → {q.clicks}</span>
                        <span className={`w-14 text-right font-semibold inline-flex items-center justify-end gap-0.5 rounded-full ${q.delta > 0 ? "text-emerald-400" : "text-red-400"}`}>
                          {q.delta > 0 ? <ArrowUp size={11} weight="bold" /> : <ArrowDown size={11} weight="bold" />}
                          {q.delta > 0 ? `+${q.delta}` : q.delta}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-white/35 mt-2">Variazione clic: ultimi 28 giorni rispetto ai 28 precedenti.</p>
                </div>
              )}
              {detailSite.top_pages?.length > 0 && <TopPagesList pages={detailSite.top_pages} domain={detailSite.domain} />}
              {detailSite.devices?.length > 0 && <DeviceBars devices={detailSite.devices} />}
              {detailSite.top_countries?.length > 0 && <CountryBars countries={detailSite.top_countries} />}
              {detailSite.pages?.length > 0 && (
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="site-pages-list">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-white/35">Stato pagine ({detailSite.pages.length})</p>
                    {detailSite.pages.some((p) => !p.indexed) && (
                      <button type="button" data-testid="index-missing-button" onClick={handleIndexMissing} disabled={bulkIndexing}
                        className="h-7 px-3 rounded-full text-[10px] font-semibold border border-amber-400/50 text-amber-300 hover:bg-amber-400/10 transition-colors disabled:opacity-50">
                        {bulkIndexing ? "Invio richieste a Google..." : `Indicizza mancanti (${detailSite.pages.filter((p) => !p.indexed).length})`}
                      </button>
                    )}
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
                    {detailSite.pages.map((p) => (
                      <button key={p.url} type="button" onClick={() => setIndexUrl(p.url)}
                        title={`${p.state} — clicca per richiederne l'indicizzazione`}
                        data-testid={`page-status-${p.url}`}
                        className="w-full flex items-center gap-2 text-left text-xs rounded-md px-2 py-1.5 hover:bg-white/5 transition-colors">
                        {p.indexed
                          ? <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0" />
                          : <WarningCircle size={14} weight="fill" className="text-amber-400 shrink-0" />}
                        <span className="truncate flex-1 text-white/70">{p.url.replace(/^https?:\/\/(www\.)?/, "")}</span>
                        <span className={`shrink-0 text-[10px] ${p.indexed ? "text-emerald-400/70" : p.index_requested_at ? "text-sky-300/80" : "text-amber-400/80"}`}>
                          {p.indexed ? "indicizzata" : p.index_requested_at ? "richiesta inviata" : (p.state || "non indicizzata")}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="site-history-section">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/35">Andamento clic (storico)</p>
                  <div className="flex gap-1">
                    {[{ d: 28, l: "28gg" }, { d: 90, l: "3 mesi" }, { d: 180, l: "6 mesi" }, { d: 365, l: "1 anno" }].map((p) => (
                      <button key={p.d} type="button" data-testid={`history-period-${p.d}`}
                        onClick={() => changeHistoryDays(p.d)}
                        className={`h-6 px-2.5 rounded-full text-[10px] font-semibold border transition-colors ${historyDays === p.d ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:text-white"}`}>
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>
                {history === null && <p className="text-xs text-white/40 py-4 text-center">Caricamento storico...</p>}
                {history && history.rows?.length === 0 && (
                  <p className="text-xs text-white/40 py-4 text-center" data-testid="history-empty">Nessuno storico ancora: si costruisce a ogni aggiornamento giornaliero.</p>
                )}
                {history && history.rows?.length > 0 && (
                  <>
                    <div className="flex items-end gap-px h-20" data-testid="history-chart">
                      {history.rows.map((r) => {
                        const max = Math.max(...history.rows.map((x) => x.clicks), 1);
                        return (
                          <div key={r.date} title={`${new Date(r.date).toLocaleDateString("it-IT")}: ${r.clicks} clic, ${r.impressions} impressioni`}
                            className="flex-1 min-w-[1px] rounded-t-sm bg-sky-400/70 hover:bg-sky-300"
                            style={{ height: `${Math.max((r.clicks / max) * 100, 2)}%` }} />
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-white/40 mt-2" data-testid="history-totals">
                      {history.rows.length} giorni registrati — {history.total_clicks} clic, {history.total_impressions} impressioni nel periodo
                    </p>
                    {history.rows.some((r) => r.indexed !== undefined) && (
                      <div className="mt-3" data-testid="indexed-history">
                        <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 mb-1">Pagine indicizzate nel tempo</p>
                        <div className="flex items-end gap-px h-10">
                          {history.rows.map((r) => {
                            const maxIdx = Math.max(...history.rows.map((x) => x.total_pages || x.indexed || 0), 1);
                            if (r.indexed === undefined) return <div key={r.date} className="flex-1 min-w-[1px]" />;
                            const full = r.total_pages && r.indexed >= r.total_pages;
                            return (
                              <div key={r.date} title={`${new Date(r.date).toLocaleDateString("it-IT")}: ${r.indexed}${r.total_pages ? `/${r.total_pages}` : ""} indicizzate`}
                                className={`flex-1 min-w-[1px] rounded-t-sm ${full ? "bg-emerald-400/80" : "bg-amber-400/70"}`}
                                style={{ height: `${Math.max((r.indexed / maxIdx) * 100, 4)}%` }} />
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-white/35 mt-1">Verde = tutte le pagine della sitemap indicizzate, ambra = alcune mancanti. Registrato a ogni aggiornamento.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="site-note-section">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/35 flex items-center gap-1 mb-2"><NotePencil size={11} /> Promemoria</p>
                <Textarea data-testid="site-note-textarea" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                  placeholder={'Es. "segnalato a support il 19/08"'}
                  className="bg-transparent border-white/10 text-white placeholder:text-white/25 text-sm" />
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-white/35">
                    {data?.notes?.[detailSite.domain] ? `Aggiornato ${new Date(data.notes[detailSite.domain].updated_at).toLocaleDateString("it-IT")} da ${data.notes[detailSite.domain].updated_by || "—"}` : "Nessun promemoria salvato"}
                  </p>
                  <Button data-testid="site-note-save-button" onClick={handleSaveNote} disabled={savingNote}
                    variant="ghost" className="h-8 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                    {savingNote ? "Salvataggio..." : "Salva promemoria"}
                  </Button>
                </div>
              </div>
              <div className="space-y-2 pt-1">
                <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Verifica o richiedi indicizzazione di un URL</Label>
                <Input data-testid="indexing-url-input" value={indexUrl} onChange={(e) => setIndexUrl(e.target.value)}
                  placeholder={`https://${detailSite.domain}/pagina`}
                  className="h-11 bg-transparent border-white/10 text-white placeholder:text-white/25" />
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="inspect-url-button" onClick={handleInspect} disabled={inspecting || !indexUrl}
                    variant="ghost" className="h-10 px-5 border border-sky-400/40 text-sky-300 hover:bg-sky-400/10 hover:text-sky-200 rounded-full font-semibold text-sm">
                    <ArrowsClockwise size={15} className={`mr-1.5 ${inspecting ? "animate-spin" : ""}`} /> {inspecting ? "Controllo..." : "Ricontrolla ora"}
                  </Button>
                  <Button data-testid="indexing-submit-button" onClick={handleRequestIndexing} disabled={indexing || !indexUrl}
                    className="h-10 px-5 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
                    <PaperPlaneTilt size={15} className="mr-1.5" /> {indexing ? "Invio..." : "Richiedi indicizzazione"}
                  </Button>
                </div>
                {inspectResult && (
                  <div className={`p-3 rounded-lg border text-xs space-y-1 ${inspectResult.indexed ? "border-emerald-400/30 bg-emerald-400/5" : "border-amber-400/30 bg-amber-400/5"}`} data-testid="inspect-result">
                    <p className={`flex items-center gap-1.5 font-semibold ${inspectResult.indexed ? "text-emerald-400" : "text-amber-300"}`}>
                      {inspectResult.indexed ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
                      {inspectResult.coverage_state || (inspectResult.indexed ? "Indicizzata" : "Non indicizzata")}
                    </p>
                    <p className="text-white/50">
                      Scaricamento pagina: <span className={inspectResult.page_fetch_state === "SUCCESSFUL" ? "text-emerald-400" : "text-amber-300"}>{inspectResult.page_fetch_state === "SUCCESSFUL" ? "riuscito" : inspectResult.page_fetch_state || "—"}</span>
                      {" — "}robots.txt: {inspectResult.robots_txt_state === "ALLOWED" ? "ok" : inspectResult.robots_txt_state || "—"}
                      {inspectResult.last_crawl_time ? ` — ultima scansione: ${new Date(inspectResult.last_crawl_time).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : ""}
                    </p>
                  </div>
                )}
                <p className="text-[11px] text-white/35">"Ricontrolla ora" interroga Google in tempo reale (stesso dato del Controllo URL di Search Console) e aggiorna l'elenco pagine.</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {doctorHistory.length > 0 && (
        <div className="mt-10 animate-fade-up" data-testid="site-doctor-history">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-1.5">
            <Stethoscope size={13} className="text-emerald-400" /> Storico visite Doctor
          </p>
          <div className="border border-surface-border rounded-lg bg-surface overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-white/30 border-b border-white/5">
              <span className="w-32">Data e ora</span>
              {histDomains.map((d) => <span key={d} className="w-20 text-center truncate hidden sm:block">{d.replace(".it", "")}</span>)}
              <span className="ml-auto">Esito</span>
            </div>
            <div className="max-h-72 overflow-y-auto divide-y divide-white/5">
              {doctorHistory.map((h, i) => {
                const doms = Object.keys(h.summary || {});
                const sick = doms.filter((d) => (h.summary[d]?.overall || "unknown") !== "ok").length;
                return (
                  <div key={h.checked_at} className="flex items-center gap-2 px-4 py-2.5 text-xs" data-testid={`site-doctor-history-row-${i}`}>
                    <span className="w-32 text-white/60">{new Date(h.checked_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</span>
                    {histDomains.map((d) => {
                      const st = h.summary?.[d]?.overall || "unknown";
                      return (
                        <span key={d} className="w-20 justify-center hidden sm:flex" title={`${d}: ${DOT_LABEL[st]}${h.summary?.[d]?.latency_ms != null ? ` · ${h.summary[d].latency_ms}ms` : ""}`}>
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: DOT_COLOR[st] }} />
                        </span>
                      );
                    })}
                    <span className={`ml-auto font-semibold ${sick === 0 ? "text-emerald-400" : "text-amber-300"}`}>
                      {sick === 0 ? "Tutto OK" : `${sick} con problemi`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-[10px] text-white/35 mt-2">Ogni riga è una visita Doctor (manuale o automatica del mattino): i pallini mostrano lo stato di ciascun sito in quel momento.</p>
        </div>
      )}

      {autoIndexHistory.length > 0 && (
        <div className="mt-10 animate-fade-up" data-testid="auto-index-trend">
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-1.5">
            <PaperPlaneTilt size={13} className="text-sky-400" /> Trend indicizzazione notturna
          </p>
          <div className="border border-surface-border rounded-lg bg-surface p-5">
            <div className="flex flex-wrap gap-x-6 gap-y-1 mb-4 text-xs" data-testid="auto-index-trend-summary">
              <span className="text-emerald-400 font-semibold">{autoIndexHistory.reduce((a, h) => a + (h.fixed || 0), 0)} pagine recuperate in totale</span>
              <span className="text-white/50">Ultima notte: {autoIndexHistory[autoIndexHistory.length - 1].fixed} recuperate · {autoIndexHistory[autoIndexHistory.length - 1].requested} richieste inviate · {autoIndexHistory[autoIndexHistory.length - 1].missing} ancora escluse</span>
            </div>
            <div className="h-52" data-testid="auto-index-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={autoIndexHistory.map((h) => ({
                  date: new Date(h.at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }),
                  Recuperate: h.fixed || 0,
                  "Ancora escluse": h.missing || 0,
                  Richieste: h.requested || 0,
                }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#1A1A24", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: "rgba(255,255,255,0.6)" }} itemStyle={{ color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }} />
                  <Bar dataKey="Recuperate" fill="#34D399" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  <Line type="monotone" dataKey="Ancora escluse" stroke="#FBBF24" strokeWidth={2} dot={{ r: 3, fill: "#FBBF24" }} />
                  <Line type="monotone" dataKey="Richieste" stroke="#38BDF8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-white/35 mt-3">Ogni notte il portale richiede a Google l'indicizzazione delle pagine ancora escluse: le barre verdi sono le pagine tornate nell'indice rispetto alla notte precedente, la linea gialla quelle ancora escluse, la linea azzurra le richieste inviate.</p>
          </div>
        </div>
      )}

      <Dialog open={doctorOpen} onOpenChange={setDoctorOpen}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-lg" data-testid="site-doctor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <Stethoscope size={20} weight="duotone" className="text-emerald-400" /> Doctor — Stato di salute dei siti
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Controllo in diretta: raggiungibilità, tempi di risposta, robots.txt e sitemap.xml di ogni sito monitorato.
            </DialogDescription>
          </DialogHeader>
          {doctorRunning ? (
            <div className="py-10 text-center" data-testid="site-doctor-loading">
              <ArrowsClockwise size={28} className="animate-spin text-white/40 mx-auto mb-3" />
              <p className="text-sm text-white/50">Visita in corso sui {5} siti...</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {(doctorData?.results || []).map((r) => {
                const color = r.overall === "ok" ? "#34D399" : r.overall === "warn" ? "#FBBF24" : "#EF4444";
                const Chip = ({ ok, warnLabel = "Mancante", label }) => (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 ${ok ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-amber-400/15 text-amber-300 border-amber-400/30"}`}>
                    {label}: {ok ? "OK" : warnLabel}
                  </span>
                );
                return (
                  <div key={r.domain} data-testid={`site-doctor-${r.domain}`}
                    className="p-3 rounded-lg bg-white/[0.03] border border-white/10 border-l-4" style={{ borderLeftColor: color }}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold">{r.domain}</p>
                      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color }}>
                        {r.overall === "ok" ? "In salute" : r.overall === "warn" ? "Attenzione" : "Non raggiungibile"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 ${r.site === "ok" ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "bg-red-500/15 text-red-400 border-red-500/30"}`}>
                        Sito: {r.site === "ok" ? `${r.http_status} · ${r.latency_ms}ms` : r.site === "warn" ? `Errore ${r.http_status}` : "Non risponde"}
                      </span>
                      <Chip ok={r.robots === "ok"} label="robots.txt" />
                      <Chip ok={r.sitemap === "ok"} label="sitemap.xml" />
                    </div>
                  </div>
                );
              })}
              {doctorData?.checked_at && (
                <p className="text-[10px] text-white/35 text-center pt-1">
                  Visita eseguita: {new Date(doctorData.checked_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" })}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
